import type {
    Player,
    PlayerOptions,
    PlayerZoomTransform,
} from '@mfc/mfc-video-sdk'
import { VIDEOSTATE } from '..'
import type { ViewModeButtons } from '../../../types/state'

/** SDK control-bar button names (`Player.Controls`). The class loads lazily,
 *  so the names are mirrored here instead of imported. */
export const CHAT_OVERLAY_BUTTON = 'ChatOverlayButton'
export const THEATER_MODE_BUTTON = 'TheaterModeButton'

// The viewer's zoom from just before a full-window click, which the SDK
// replaces with its own theater mode's cover zoom and resets to 100% when
// the buttons take it back out of that mode
const zoomBeforeTheaterMode = new WeakMap<Player, PlayerZoomTransform>()

/**
 * Put a placeholder's view-mode buttons on its player. Runs when the top
 * placeholder's options change and after a rebuild's render(), after the
 * zoom options: the SDK's portrait theater mode lights the theater-mode
 * icon and clears it on exit.
 */
export const applyViewModeButtons = (
    instance: Player,
    {
        chatOverlayButton = 'hidden',
        theaterModeButton = 'hidden',
    }: ViewModeButtons,
    portraitTheater: boolean,
) => {
    instance.showButton(CHAT_OVERLAY_BUTTON, chatOverlayButton !== 'hidden')
    instance.showButton(THEATER_MODE_BUTTON, theaterModeButton !== 'hidden')
    // shouldNotify stays false: the app is the source of truth here, so
    // the toggles must not fire the handlers back into setRoomViewMode
    instance.setChatOverlay(chatOverlayButton === 'on', false)
    // the SDK's own theater mode brings a "cover" zoom that crops streams
    // narrower than the box; the app's FullWindow never cropped, so keep
    // the SDK's mode off (an enter click turns it on before the handler
    // runs; this turns it back off) and light only the button. The same
    // call exits the SDK's portrait theater mode, which the Portrait
    // layout's zoomed level relies on, so skip it for that player.
    if (!portraitTheater) {
        instance.setTheaterMode(false, false)
        const zoom = zoomBeforeTheaterMode.get(instance)
        if (zoom) {
            zoomBeforeTheaterMode.delete(instance)
            instance.setZoomTransform(zoom)
        }
    }
    instance.toggleTheaterModeIcon(theaterModeButton === 'on')
}

export type VideoViewModeHandlers = Pick<
    PlayerOptions,
    'theaterModeHandler' | 'chatOverlayHandler'
>

// The handlers (actions/videoViewModeControls) switch the room's view mode
// through the router, the transitions and the room state, and that graph
// imports state.video back. They are registered at app setup
// (setupSubscriptions) instead of imported here, so state.video, which
// spreads them into every Player it builds, stays out of it.
let handlers: ((id: number) => VideoViewModeHandlers) | null = null

export const setVideoViewModeHandlers = (
    factory: ((id: number) => VideoViewModeHandlers) | null,
) => {
    handlers = factory
}

/**
 * The SDK button handlers for a player's constructor options. They look the
 * registered ones up per click rather than at construction: the SDK creates
 * the buttons only for handlers it was given, so a player built before app
 * setup still gets them.
 */
export const videoViewModeHandlers = (id: number): VideoViewModeHandlers => ({
    theaterModeHandler: (on: boolean) => {
        // the SDK calls this before it applies its cover zoom
        const instance = VIDEOSTATE.videos[id]?.instance
        const zoom = on && instance?.getZoomTransform()
        if (zoom) zoomBeforeTheaterMode.set(instance, zoom)
        handlers?.(id).theaterModeHandler?.(on)
    },
    chatOverlayHandler: (on: boolean) =>
        handlers?.(id).chatOverlayHandler?.(on),
})
